import type { KeyboardEventHandler } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Collapse,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { uniq } from 'ramda';
import type { AllEntities } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type { SearchPayload } from 'c-data-layer';
import { NetworkRequestState } from 'c-data-layer';
import { useDebouncedCallback } from 'use-debounce';
import equal from 'fast-deep-equal';
import { useBoolean, usePrevious } from 'react-hanger';
import { getBaseSliceThunks, usePaginatedEntityData } from 'c-data';
import { AutoGrid, Button, LoadingSpinner, TextField } from 'c-components';
import { Translate, useCommonTranslation } from 'c-translation';
import { FilterAlt, Search } from '@mui/icons-material';
import { IfHasAllPermissions, IfUserIsOneOfTypes } from 'c-auth-module/Components';
import type {
    FilterableEntityTableProps,
    FilterConfig,
    FilterConfigRendererProps,
} from 'c-pagination/types';
import { CustomFilterConfigType } from 'c-pagination/types';
import type { SearchTermSetter } from 'c-pagination/EntityPaginationContext';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import to from 'await-to-js';
import { fieldBackdropColor } from 'c-lib';
import GroupedSelectFilter from 'c-pagination/Components/EntityFilters/GroupedSelectFilter';
import DropdownFilter from './DropdownFilter';
import DateFilter from './DateFilter';
import DateRangeFilter from './DateRangeFilter';
import EntityAutocompleteFilter from './EntityAutocompleteFilter';
import TextFilter from './TextFilter';

type Props = {
    entityName: keyof AllEntities;
    tag: string;
    allFilters: FilterConfig[];
    textSearchColumn?: string;
    textSearchCols?: string[];
    reset: () => void;
    onSubmit: (searchVal: string, force?: boolean) => void;

    defaultSearchVal: string;

    /**
     * Hands this component's search-term setter to the owning table, so it can offer it on its
     * context for cells that want to search for something they render. Called with `undefined` on
     * unmount. Must be stable — it is an effect dependency.
     */
    registerSearchTermSetter?: (setter?: SearchTermSetter) => void;

    afterFilters?: FilterableEntityTableProps['afterFilters'];
    alwaysOpenFilters?: boolean;
    standaloneResetFilters?: boolean;
    filtersInlineSearch?: boolean;
    displayDownloadButton?: boolean;
    reImportButton?: boolean;
    customFilters?: React.ReactNode[];
    customFilterResets: (() => void)[];
    filterApplyButton: boolean;
};

// Both buttons act on Displays_Screen specifically, so they carry the displays admin permissions.
const csvExportPerms = [PermissionName.AdminDisplaysCsv_export];
const reImportPerms = [PermissionName.AdminDisplaysReimport];

const searchSizes = {
    xs: 12,
    sm: 8,
    md: 6,
    lg: 4,
    xl: 3,
};

const afterSearchProps = {
    xs: 12,
    sm: 12 - searchSizes.sm,
    md: 12 - searchSizes.md,
    lg: 12 - searchSizes.lg,
    xl: 12 - searchSizes.xl,
};

const afterSearchBoxProps = {
    sx: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
};

const defaultFilterCount = (value: any[]) => (Array.isArray(value) ? uniq(value).length : 0);

const filterComponentsMap = {
    [CustomFilterConfigType.Select]: DropdownFilter,
    [CustomFilterConfigType.Date]: DateFilter,
    [CustomFilterConfigType.DateRange]: DateRangeFilter,
    [CustomFilterConfigType.EntityAutocomplete]: EntityAutocompleteFilter,
    [CustomFilterConfigType.Text]: TextFilter,
    [CustomFilterConfigType.GroupedSelect]: GroupedSelectFilter,
};

/**
 * CSV export and re-import act on whatever the list is narrowed to, so they stay off until a
 * filter or a search narrows it; this is the reason shown on them until then.
 */
export const exportNeedsFilterOrSearch = (
    filters: Record<string, unknown> | null | undefined,
    searchTerm: string | null | undefined,
) => Object.keys(filters ?? {}).length === 0 && (searchTerm ?? '').length === 0;

/** A disabled button can't show a tooltip itself, so the reason goes on a wrapper. */
const WithDisabledReason: React.FC<React.PropsWithChildren<{ reason: string | null }>> = ({
    reason,
    children,
}) =>
    reason == null ? (
        <>{children}</>
    ) : (
        <Tooltip title={reason}>
            <span>{children}</span>
        </Tooltip>
    );

const actualConfigValue = (
    config: FilterConfig,
    allFilterValues: SearchPayload<any>['filters'],
) => {
    let filterValue = [];
    if (Array.isArray(allFilterValues?.[config.key])) {
        filterValue = allFilterValues?.[config.key];
    }

    return uniq(filterValue);
};

const EntityFilters: React.FC<Props> = ({
    entityName,
    tag,
    textSearchColumn,
    textSearchCols = [],
    reset,
    defaultSearchVal,
    onSubmit,
    registerSearchTermSetter,

    afterFilters,
    allFilters,

    alwaysOpenFilters = false,
    standaloneResetFilters = false,
    filtersInlineSearch = false,
    displayDownloadButton = false,
    reImportButton = false,
    customFilters = [],
    customFilterResets = [],
    filterApplyButton = false,
}) => {
    const t = useCommonTranslation();
    const { paginatedData } = usePaginatedEntityData(tag, entityName);
    const [searchVal, setSearchVal] = useState(defaultSearchVal);
    const filterOpenState = useBoolean(alwaysOpenFilters);

    const {
        start,
        data,
        requestState,
        reset: requestReset,
    } = useAPIClientRequest(ApiClient.Entities.Displays_Screen.csvExport);
    const {
        start: startReImport,
        data: reImportData,
        requestState: reImportRequestState,
        reset: reImportReset,
    } = useAPIClientRequest(ApiClient.Entities.Displays_Screen.reImport);
    const getData = useCallback(async () => {
        const searchFilters = textSearchCols.reduce((acc, col) => {
            acc[`search.${col}`] = searchVal;
            return acc;
        }, {});

        await to(
            start({
                ...searchFilters,
                ...paginatedData.filters,
            }),
        );
    }, [paginatedData.filters, start, searchVal, textSearchCols]);
    const getReImportData = useCallback(async () => {
        const searchFilters = textSearchCols.reduce((acc, col) => {
            acc[`search.${col}`] = searchVal;
            return acc;
        }, {});
        await to(
            startReImport({
                ...searchFilters,
                ...paginatedData.filters,
            }),
        );
    }, [textSearchCols, startReImport, paginatedData.filters, searchVal]);
    const compactActionButtonSx = useMemo(
        () => ({
            minHeight: 26,
            minWidth: 0,
            px: 1,
            py: 0.3,
            fontSize: '0.72rem',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
        }),
        [],
    );
    const compactDismissButtonSx = useMemo(
        () => ({
            minHeight: 24,
            minWidth: 20,
            px: 0.4,
            py: 0.2,
            lineHeight: 1,
        }),
        [],
    );

    const exportDisabledReason = exportNeedsFilterOrSearch(paginatedData.filters, searchVal)
        ? t('Pagination.exportNeedsFilterOrSearch')
        : null;

    const applyButton = useMemo(() => {
        const handleDownloadClick = async () => {
            await getData();
        };
        if (requestState === NetworkRequestState.InProgress) {
            return <LoadingSpinner />;
        }
        if (requestState === NetworkRequestState.Success) {
            return (
                <Stack direction="row">
                    <Typography fontWeight={0}>{(data as any)?.data.message}</Typography>
                    <Button
                        size="small"
                        variant="text"
                        sx={compactDismissButtonSx}
                        onClick={() => requestReset()}
                    >
                        x
                    </Button>
                </Stack>
            );
        }
        return (
            <WithDisabledReason reason={exportDisabledReason}>
                <Button
                    size="small"
                    sx={compactActionButtonSx}
                    disabled={exportDisabledReason != null}
                    onClick={handleDownloadClick}
                >
                    {t('Modules.Displays.DisplaysList.downloadCSV')}
                </Button>
            </WithDisabledReason>
        );
    }, [
        requestState,
        exportDisabledReason,
        t,
        getData,
        data,
        requestReset,
        compactActionButtonSx,
        compactDismissButtonSx,
    ]);

    const reImportButtonComponent = useMemo(() => {
        const handleReImportClick = async () => {
            await getReImportData();
        };
        if (reImportRequestState === NetworkRequestState.InProgress) {
            return <LoadingSpinner />;
        }
        if (reImportRequestState === NetworkRequestState.Success) {
            return (
                <Stack direction="row">
                    <Typography fontWeight={0}>{(reImportData as any)?.data.message}</Typography>
                    <Button
                        size="small"
                        variant="text"
                        sx={compactDismissButtonSx}
                        onClick={() => reImportReset()}
                    >
                        x
                    </Button>
                </Stack>
            );
        }
        return (
            <WithDisabledReason reason={exportDisabledReason}>
                <Button
                    size="small"
                    sx={compactActionButtonSx}
                    disabled={exportDisabledReason != null}
                    onClick={handleReImportClick}
                >
                    {t('Modules.Displays.DisplaysList.reImport')}
                </Button>
            </WithDisabledReason>
        );
    }, [
        getReImportData,
        exportDisabledReason,
        reImportData,
        reImportRequestState,
        reImportReset,
        t,
        compactActionButtonSx,
        compactDismissButtonSx,
    ]);

    const onSearchChange = useCallback(e => {
        setSearchVal(e.target.value);
    }, []);

    /**
     * Filter changes and search-term changes get a debounce each, and must keep getting one each.
     *
     * They shared a single `useDebouncedCallback` slot, and scheduling onto a debounce *replaces*
     * whatever is pending — so whichever effect ran last silently discarded the other's work. The
     * list polls itself, and every poll hands down a new `paginatedData` object, which re-runs the
     * filters effect on its own; land one inside the search term's debounce window and the term is
     * dropped. The box then shows one value while every request carries the previous one, for as
     * long as nothing else re-triggers a search.
     */
    const doSearch = useDebouncedCallback((callback: () => void) => {
        callback();
    }, 1000);

    const doTermSearch = useDebouncedCallback((callback: () => void) => {
        callback();
    }, 1000);

    const prevFilters = usePrevious(paginatedData.filters);
    const prevSearch = usePrevious(searchVal);

    const doTheSearch = useCallback(
        (force = false) => {
            onSubmit(searchVal, force);
        },
        [searchVal, onSubmit],
    );
    const applyFiltersButton = useMemo(
        () => (
            <Button
                size="small"
                sx={compactActionButtonSx}
                onClick={() =>
                    doSearch(() => {
                        doTheSearch();
                    })
                }
            >
                {t('Modules.Main.CreativeManagement.creatives.filters.apply')}
            </Button>
        ),
        [doSearch, doTheSearch, t, compactActionButtonSx],
    );

    // const submitBtnRef = useRef<HTMLButtonElement>();

    /**
     * The filters as they are now, and the search this render would send, for a debounced filter
     * search to read when it fires rather than when it was queued.
     */
    const latestFilters = useRef(paginatedData.filters);
    latestFilters.current = paginatedData.filters;
    const latestDoTheSearch = useRef(doTheSearch);
    latestDoTheSearch.current = doTheSearch;
    /**
     * The filters the last search went out with. Every search start writes a new `loadingState`
     * (and the filters it searched); a filter edit leaves `loadingState` as it was.
     */
    const searchedFilters = useRef(paginatedData.filters);
    useEffect(() => {
        if (paginatedData.loadingState?.state === NetworkRequestState.InProgress) {
            searchedFilters.current = latestFilters.current;
        }
    }, [paginatedData.loadingState]);

    useEffect(() => {
        const filters = paginatedData.filters;
        /**
         * Queue up a delayed (debounced) search when the filters are updated.
         *
         * Queue only on a change, and decide when it fires: this effect runs again on the next
         * render (`prevFilters` catches up), and scheduling replaces what is pending. Queuing on
         * every run put a no-op over the real search, so a list response landing within the
         * debounce (a poll, the search Back restores) dropped the user's filter change. When it
         * fires, a search that already went out with these filters (the table's own: a reset on
         * mount, a restore from the URL) leaves nothing to do; searching again would go back to
         * page 1 with whatever ordering the queuing render had.
         */
        if (!filterApplyButton && filters != null && !equal(filters, prevFilters)) {
            doSearch(() => {
                const current = latestFilters.current;
                if (current != null && !equal(current, searchedFilters.current)) {
                    latestDoTheSearch.current();
                }
            });
        }
    }, [paginatedData.filters, doSearch, prevFilters, filterApplyButton]);

    /**
     * A term the table put in the box itself (restoring it from the URL on Back), which it has
     * already searched for. The searchVal effect skips it rather than searching for it again on
     * page 1, which would throw away the page being restored.
     */
    const silentTerm = useRef<string | undefined>(undefined);

    // Hand the setter to the owning table. Writing through it queues the same debounced search a
    // keystroke does (see the searchVal effect below), so callers never trigger one themselves.
    const setSearchTerm = useCallback<SearchTermSetter>(
        (term, { silent = false } = {}) => {
            if (silent) {
                silentTerm.current = term;
                doTermSearch.cancel();
            }
            setSearchVal(term);
        },
        [doTermSearch],
    );
    useEffect(() => {
        if (!registerSearchTermSetter) return undefined;

        registerSearchTermSetter(setSearchTerm);

        return () => registerSearchTermSetter(undefined);
    }, [registerSearchTermSetter, setSearchTerm]);

    useEffect(() => {
        const val = searchVal;
        if (silentTerm.current !== undefined) {
            const isSilent = silentTerm.current === val;
            silentTerm.current = undefined;
            if (isSilent) return;
        }
        /**
         * Queue up a delayed (debounced) search when the search value is updated
         */
        if (!filterApplyButton) {
            doTermSearch(() => {
                if (val != null && val !== prevSearch && prevSearch != null) {
                    doTheSearch();
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchVal, doTermSearch, filterApplyButton]);

    const onClickReset = useCallback(() => {
        setSearchVal('');
        customFilterResets.forEach(resetFn => resetFn());
        reset();
    }, [customFilterResets, reset]);
    const resetButton = useMemo(
        () => (
            <Button
                size="small"
                onClick={onClickReset}
                sx={{
                    minHeight: 'auto',
                    minWidth: 0,
                    px: 1.5,
                    py: 0.4,
                    borderRadius: '999px',
                }}
            >
                <Typography variant="caption">
                    <Translate path="Pagination.resetFilters" />
                </Typography>
            </Button>
        ),
        [onClickReset],
    );

    const onSearchKeyUp = useCallback<KeyboardEventHandler<any>>(
        event => {
            if (event.key === 'Enter') {
                // Enter has searched for this term, so the search-as-you-type one still pending
                // for it would only repeat it, from page 1, after the user may have paged on
                doTermSearch.cancel();
                doTheSearch(true);
            }
        },
        [doTermSearch, doTheSearch],
    );

    const searchPlaceholder = useCommonTranslation('Pagination.searchPlaceholder');
    const searchInput = useMemo(
        () => (
            <>
                <TextField
                    placeholder={searchPlaceholder}
                    onChange={onSearchChange}
                    onKeyUp={onSearchKeyUp}
                    value={searchVal}
                    size="small"
                    id={`search-${tag}`}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        endAdornment: standaloneResetFilters ? undefined : (
                            <InputAdornment position="end" sx={{ mr: 0.5 }}>
                                {resetButton}
                            </InputAdornment>
                        ),
                    }}
                />
            </>
        ),
        [
            searchPlaceholder,
            onSearchChange,
            resetButton,
            searchVal,
            tag,
            onSearchKeyUp,
            standaloneResetFilters,
        ],
    );

    const appliedFiltersCount = useMemo(
        () =>
            allFilters.reduce((count, config) => {
                const actualValue = actualConfigValue(config, paginatedData?.filters ?? {});

                let filterCount = defaultFilterCount(actualValue);

                if (config.filterCountOverride) {
                    filterCount = config.filterCountOverride(
                        filterCount,
                        actualValue,
                        paginatedData?.filters ?? {},
                    );
                }

                if (filterCount > 0) return count + 1;

                return count;
            }, 0),
        [paginatedData?.filters, allFilters],
    );

    const filterButton = useMemo(
        () => (
            <Badge badgeContent={appliedFiltersCount} color="primary" max={99}>
                <IconButton
                    onClick={filterOpenState.toggle}
                    className="filter-toggle"
                    aria-label={t('Pagination.toggleFiltersLabel')}
                    title={t('Pagination.toggleFiltersLabel')}
                    aria-expanded={filterOpenState.value}
                >
                    <FilterAlt fontSize="inherit" />
                </IconButton>
            </Badge>
        ),
        [filterOpenState.toggle, filterOpenState.value, appliedFiltersCount, t],
    );

    /**
     * Reset presented as its own control after the inputs rather than tucked inside the search
     * box. `appliedFiltersCount` is used rather than the raw filter state because that state keeps
     * emptied keys around, which would leave the button showing with nothing to reset.
     */
    const standaloneResetButton = useMemo(() => {
        if (!standaloneResetFilters || (appliedFiltersCount === 0 && searchVal.length === 0)) {
            return null;
        }

        return (
            <Grid item {...searchSizes}>
                <Button size="small" variant="text" onClick={onClickReset}>
                    <Translate path="Pagination.resetFilters" />
                </Button>
            </Grid>
        );
    }, [standaloneResetFilters, appliedFiltersCount, searchVal.length, onClickReset]);

    const allFilterComponents = useMemo(() => {
        const filterComponents = allFilters.map((f, idx) => (
            <Box key={f.key}>
                <IfUserIsOneOfTypes roles={f.userTypes} forceShow>
                    <IfHasAllPermissions permissions={f.permissions} forceShow>
                        <Box sx={{ bgcolor: fieldBackdropColor, borderRadius: '8px' }}>
                            <CustomConfigFiltersWrapper
                                entityName={entityName}
                                tag={tag}
                                config={f}
                                Component={filterComponentsMap[f.type]}
                            />
                        </Box>
                    </IfHasAllPermissions>
                </IfUserIsOneOfTypes>
            </Box>
        ));
        if (customFilters.length > 0) {
            customFilters.map(filter => filterComponents.push(<Box>{filter}</Box>));
        }
        const buttons = [];
        if (filterApplyButton) {
            buttons.push(
                <Box pl={1} key="applyFiltersButton">
                    {applyFiltersButton}
                </Box>,
            );
        }
        if (displayDownloadButton) {
            buttons.push(
                <Box key="applyButton" ml={1}>
                    <IfHasAllPermissions permissions={csvExportPerms}>
                        {applyButton}
                    </IfHasAllPermissions>
                </Box>,
            );
        }
        if (reImportButton) {
            buttons.push(
                <Box key="reImportButton" ml={1}>
                    <IfHasAllPermissions permissions={reImportPerms}>
                        {reImportButtonComponent}
                    </IfHasAllPermissions>
                </Box>,
            );
        }
        if (buttons.length > 0) {
            const buttonList = (
                <AutoGrid key="filterActionButtons" gap={1} xs={2} columns={buttons.length}>
                    {buttons.map(button => (
                        <Box key={button.key}>{button}</Box>
                    ))}
                </AutoGrid>
            );
            filterComponents.push(buttonList);
        }

        return filterComponents;
    }, [
        allFilters,
        customFilters,
        filterApplyButton,
        displayDownloadButton,
        reImportButton,
        entityName,
        tag,
        applyFiltersButton,
        applyButton,
        reImportButtonComponent,
    ]);

    const collapsibleFilterComponents = useMemo(
        () => (
            <>
                <Collapse in={filterOpenState.value}>
                    <Box mt={1}>
                        <AutoGrid spacing={2} xs={12} sm={6} md={4} lg={3} xl={2}>
                            {allFilterComponents}
                        </AutoGrid>
                    </Box>
                </Collapse>
            </>
        ),
        [filterOpenState.value, allFilterComponents],
    );

    const afterFiltersWithWrapper = useMemo(() => {
        if (afterFilters) {
            return (
                <Grid item {...afterSearchProps}>
                    <Box {...afterSearchBoxProps}>{afterFilters}</Box>
                </Grid>
            );
        }
        return null;
    }, [afterFilters]);

    if (allFilters.length === 0 && textSearchColumn == null) {
        return null;
    }

    if (allFilters.length === 0) {
        return (
            <>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item {...searchSizes}>
                        {searchInput}
                    </Grid>
                    {afterFiltersWithWrapper}
                    {standaloneResetButton}
                </Grid>
            </>
        );
    }

    return (
        <>
            <Box mt={1}>
                {textSearchColumn && (
                    <Grid container spacing={2}>
                        <Grid item {...searchSizes}>
                            <Box display="flex">
                                {searchInput}
                                {!alwaysOpenFilters && !filtersInlineSearch && (
                                    <Box ml={1}>{filterButton}</Box>
                                )}
                            </Box>
                        </Grid>
                        {filtersInlineSearch &&
                            allFilterComponents.map((filter, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Grid item {...searchSizes} key={idx}>
                                    {filter}
                                </Grid>
                            ))}
                        {afterFiltersWithWrapper}
                        {standaloneResetButton}
                        <Grid item {...searchSizes} />
                    </Grid>
                )}
                {!filtersInlineSearch && collapsibleFilterComponents}
            </Box>
        </>
    );
};

type ConfigWrapperProps = {
    entityName: keyof AllEntities;
    tag: string;

    Component?: React.FC<FilterConfigRendererProps<any>>;
    config: FilterConfig;
};
const CustomConfigFiltersWrapper: React.FC<ConfigWrapperProps> = ({
    Component,
    tag,
    entityName,
    config,
}) => {
    const dispatch = useDispatch();

    const { updatePaginatedListFilter } = useMemo(
        () => getBaseSliceThunks(entityName),
        [entityName],
    );
    const { paginatedData } = usePaginatedEntityData(tag, entityName);
    const onChange = useCallback<FilterConfigRendererProps<any>['onChange']>(
        value => {
            let newValue = value;
            if (config.onChangeOverrideValue) {
                newValue = config.onChangeOverrideValue(newValue);
            }

            dispatch(updatePaginatedListFilter(tag, config.key, newValue));
        },
        [config, dispatch, updatePaginatedListFilter, tag],
    );

    const actualValue = useMemo(
        () => actualConfigValue(config, paginatedData?.filters ?? {}),
        [config, paginatedData?.filters],
    );

    const count = useMemo(() => {
        const defaultCount = defaultFilterCount(actualValue);

        return config.filterCountOverride
            ? config.filterCountOverride(defaultCount, actualValue, paginatedData?.filters ?? {})
            : defaultCount;
    }, [actualValue, config, paginatedData?.filters]);

    const filterId = useMemo(
        () => `${entityName}-${config.key}-${tag}`.replace(/\./g, '_'),
        [config.key, entityName, tag],
    );

    const filterClassName = useMemo(
        () => `${entityName}-${config.key}`.replace(/\./g, '_'),
        [config.key, entityName],
    );

    return (
        <Component
            value={actualValue}
            onChange={onChange}
            count={count}
            config={config}
            id={filterId}
            className={filterClassName}
        />
    );
};

export default EntityFilters;
